import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Users, RefreshCw, ChevronLeft, ChevronRight, Eye, Pencil,
  Plus, Trash2, Search, Copy, X, Save, ScanEye, Fingerprint, GripVertical
} from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { adminManagementApi } from '../../services/api';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [summary, setSummary] = useState(null);
  const [databases, setDatabases] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [listCollapsed, setListCollapsed] = useState(false);

  // Edit mode
  const [editing, setEditing] = useState(null);
  const [dirtyFields, setDirtyFields] = useState(new Set());

  // Instance role editing
  const [addingRole, setAddingRole] = useState(null);
  const [selectedDbRole, setSelectedDbRole] = useState(null);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [dirtyRoles, setDirtyRoles] = useState({});
  const [selectedOverride, setSelectedOverride] = useState(null);

  // Permission overrides
  const [allPermissions, setAllPermissions] = useState([]);
  const [resolvedPerms, setResolvedPerms] = useState(null);
  const [showEffective, setShowEffective] = useState(false);

  // Copy from user
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [copyDbScope, setCopyDbScope] = useState('all');
  const [copySaving, setCopySaving] = useState(false);

  // Action menu
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const { notify } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, summaryRes, dbRes, orgRes, permRes] = await Promise.all([
        adminManagementApi.getUsers(),
        adminManagementApi.getSummary(),
        adminManagementApi.getDatabases(),
        adminManagementApi.getOrganizations(),
        adminManagementApi.getPermissions()
      ]);
      setUsers(usersRes.data);
      setSummary(summaryRes.data);
      setDatabases(dbRes.data);
      setOrganizations(orgRes.data);
      setAllPermissions(permRes.data);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadUserDetail = async (userId) => {
    try {
      const res = await adminManagementApi.getUser(userId);
      setUserDetail(res.data);
      setSelectedDbRole(null);
      setResolvedPerms(null);
      setEditing(null);
      setDirtyFields(new Set());
      setDirtyRoles({});
      setEditingRoleId(null);
    } catch (err) {
      console.error('Failed to load user detail:', err);
    }
  };

  const loadResolvedPerms = async (userId, databaseId) => {
    try {
      const res = await adminManagementApi.resolvePermissions(userId, databaseId);
      setResolvedPerms(res.data);
    } catch (err) {
      console.error('Failed to resolve permissions:', err);
    }
  };

  const handleSelectUser = (user) => {
    setSelected(user);
    setAddingRole(null);
    setSelectedDbRole(null);
    setResolvedPerms(null);
    setShowCopyModal(false);
    setEditing(null);
    setDirtyFields(new Set());
    setDirtyRoles({});
    setEditingRoleId(null);
    setSelectedOverride(null);
    loadUserDetail(user.id);
  };

  // Edit mode
  const startEdit = () => {
    if (!userDetail) return;
    setEditing({
      displayName: userDetail.displayName,
      email: userDetail.email || '',
      role: userDetail.role,
      organizationId: userDetail.organizationId,
      isActive: userDetail.isActive
    });
    setDirtyFields(new Set());
    setDirtyRoles({});
    setEditingRoleId(null);
  };

  const cancelEdit = (force = false) => {
    if (!force && (dirtyFields.size > 0 || Object.keys(dirtyRoles).length > 0)) {
      notify.confirm('Discard unsaved changes?', () => {
        setEditing(null);
        setDirtyFields(new Set());
        setDirtyRoles({});
        setEditingRoleId(null);
      });
      return;
    }
    setEditing(null);
    setDirtyFields(new Set());
    setDirtyRoles({});
    setEditingRoleId(null);
  };

  const handleFieldChange = (field, value) => {
    setEditing(prev => ({ ...prev, [field]: value }));
    const original = field === 'organizationId' ? userDetail.organizationId
      : field === 'isActive' ? userDetail.isActive
      : field === 'role' ? userDetail.role
      : field === 'displayName' ? userDetail.displayName
      : userDetail.email || '';
    const newDirty = new Set(dirtyFields);
    if (value !== original) { newDirty.add(field); } else { newDirty.delete(field); }
    setDirtyFields(newDirty);
  };

  const handleSave = async () => {
    if (!editing || (dirtyFields.size === 0 && Object.keys(dirtyRoles).length === 0)) return;
    setSaving(true);
    try {
      if (dirtyFields.size > 0) {
        const updates = {};
        if (dirtyFields.has('displayName')) updates.DisplayName = editing.displayName;
        if (dirtyFields.has('email')) updates.Email = editing.email;
        if (dirtyFields.has('role')) updates.Role = editing.role;
        if (dirtyFields.has('organizationId')) updates.OrganizationId = editing.organizationId;
        if (dirtyFields.has('isActive')) updates.IsActive = editing.isActive;
        await adminManagementApi.updateUser(userDetail.id, updates);
      }
      for (const [userRoleId, newRole] of Object.entries(dirtyRoles)) {
        const dr = userDetail.databaseRoles?.find(r => r.id === parseInt(userRoleId));
        if (dr) {
          await adminManagementApi.updateUserRole(parseInt(userRoleId), { UserId: userDetail.id, DatabaseId: dr.databaseId, Role: newRole });
        }
      }
      setEditing(null);
      setDirtyFields(new Set());
      setDirtyRoles({});
      setEditingRoleId(null);
      await load();
      await loadUserDetail(userDetail.id);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = () => {
    if (!userDetail || !isEditing) return;
    const roleCount = userDetail.databaseRoles?.length || 0;
    const overrideCount = userDetail.databaseRoles?.reduce((sum, dr) => sum + (dr.overrides?.length || 0), 0) || 0;
    const msg = `Delete ${userDetail.displayName}?\n\nThis will remove:\n- The user record\n- ${roleCount} role${roleCount !== 1 ? 's' : ''}\n- ${overrideCount} permission${overrideCount !== 1 ? 's' : ''}`;
    notify.confirm(msg, async () => {
      setSaving(true);
      try {
        for (const dr of (userDetail.databaseRoles || [])) {
          for (const o of (dr.overrides || [])) await adminManagementApi.deleteUserPermission(o.id);
          await adminManagementApi.deleteUserRole(dr.id);
        }
        await adminManagementApi.updateUser(userDetail.id, { IsActive: false });
        setSelected(null);
        setUserDetail(null);
        setEditing(null);
        setDirtyFields(new Set());
        await load();
      } catch (err) {
        console.error('Delete failed:', err);
      } finally {
        setSaving(false);
      }
    }, { onCancel: () => cancelEdit() });
  };

  const handleSelectDbRole = (dr) => {
    setSelectedDbRole(dr);
    setSelectedOverride(null);
    if (selected) loadResolvedPerms(selected.id, dr.databaseId);
  };

  const handleAddUserRole = async () => {
    if (!addingRole || !selected || !addingRole.DatabaseId) return;
    try {
      await adminManagementApi.createUserRole(addingRole);
      setAddingRole(null);
      await loadUserDetail(selected.id);
      await load();
    } catch (err) {
      console.error('Add role failed:', err);
    }
  };

  const handleDeleteUserRole = (roleId) => {
    const dr = userDetail?.databaseRoles?.find(r => r.id === roleId);
    notify.confirm(`Remove ${dr?.databaseName || ''} role and all its permissions?`, async () => {
      try {
        await adminManagementApi.deleteUserRole(roleId);
        setSelectedDbRole(null);
        setResolvedPerms(null);
        await loadUserDetail(selected.id);
        await load();
      } catch (err) {
        console.error('Delete role failed:', err);
      }
    });
  };

  // Re-select same DB role after userDetail refresh
  useEffect(() => {
    if (selectedDbRole && userDetail?.databaseRoles) {
      const refreshed = userDetail.databaseRoles.find(dr => dr.id === selectedDbRole.id);
      if (refreshed) setSelectedDbRole(refreshed);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetail]);

  const handleCopyFrom = async () => {
    if (!copySource || !selected) return;
    setCopySaving(true);
    try {
      if (copyDbScope === 'all') {
        await adminManagementApi.copyUserRoles(copySource.id, selected.id);
      } else {
        const sourceDetail = await adminManagementApi.getUser(copySource.id);
        const sourceDbRole = sourceDetail.data.databaseRoles?.find(dr => dr.databaseId === parseInt(copyDbScope));
        if (!sourceDbRole) { alert(`${copySource.displayName} has no role for that database.`); setCopySaving(false); return; }
        let targetDbRole = userDetail.databaseRoles?.find(dr => dr.databaseId === parseInt(copyDbScope));
        if (targetDbRole) {
          await adminManagementApi.updateUserRole(targetDbRole.id, { UserId: selected.id, DatabaseId: parseInt(copyDbScope), Role: sourceDbRole.role });
        } else {
          await adminManagementApi.createUserRole({ UserId: selected.id, DatabaseId: parseInt(copyDbScope), Role: sourceDbRole.role });
        }
        const refreshed = await adminManagementApi.getUser(selected.id);
        targetDbRole = refreshed.data.databaseRoles?.find(dr => dr.databaseId === parseInt(copyDbScope));
        if (targetDbRole && sourceDbRole.overrides?.length > 0) {
          for (const o of (targetDbRole.overrides || [])) await adminManagementApi.deleteUserPermission(o.id);
          for (const o of sourceDbRole.overrides) await adminManagementApi.createUserPermission({ UserRoleId: targetDbRole.id, PermissionDefId: o.permissionDefId, AccessType: o.accessType });
        }
      }
      setShowCopyModal(false);
      setCopySource(null);
      await load();
      await loadUserDetail(selected.id);
    } catch (err) {
      console.error('Copy failed:', err);
    } finally {
      setCopySaving(false);
    }
  };

  const filteredUsers = users.filter(u =>
    !searchTerm ||
    u.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isEditing = editing !== null;
  const hasDirty = dirtyFields.size > 0 || Object.keys(dirtyRoles).length > 0;

  if (loading) return <div className="dev-page loading">Loading...</div>;

  return (
    <div className="module-container">
      <div className="module-header">
        <div className="module-header-left">
          <Users size={20} style={{ color: 'var(--color-primary)' }} />
          <h1>Admin Users</h1>
          <span className="module-header-meta">
            {summary ? `${summary.activeUsers} active / ${summary.users} total` : ''}
          </span>
        </div>
        <div className="module-header-actions">
          {/* CRUD: + or Trash depending on edit state */}
          {isEditing ? (
            <button className="crud-btn" onClick={handleDeleteUser} disabled={saving} title="Delete user" style={{ color: 'var(--color-error)' }}><Trash2 size={18} /></button>
          ) : (
            <button className="crud-btn disabled" disabled title="Add user (not yet available)"><Plus size={18} /></button>
          )}
          {/* Edit/Cancel toggle */}
          <button
            className={`crud-btn ${!isEditing && !selected ? 'disabled' : ''}`}
            onClick={isEditing ? cancelEdit : startEdit}
            disabled={!selected && !isEditing}
            title={isEditing ? 'Cancel' : 'Edit'}
          >
            {isEditing ? <X size={18} /> : <Pencil size={18} />}
          </button>
          {/* Save */}
          <button className={`crud-btn ${!hasDirty ? 'disabled' : ''}`} onClick={handleSave} disabled={!hasDirty || saving} title="Save"><Save size={18} /></button>
          {/* Action menu */}
          <div className="action-menu" ref={menuRef}>
            <button className="action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="action-menu-dropdown">
                <button className="action-menu-item" onClick={() => { setMenuOpen(false); load(); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
                <button className={`action-menu-item ${!selected ? 'disabled' : ''}`} disabled={!selected} onClick={() => { setMenuOpen(false); /* impersonate not wired */ }}>
                  <Fingerprint size={16} /><span>Impersonate User</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="list-view">
        {/* LIST PANEL */}
        {listCollapsed ? (
          <div className="list-panel-collapsed">
            <button onClick={() => setListCollapsed(false)} className="list-panel-expand-btn" title="Expand list"><ChevronRight size={16} /></button>
          </div>
        ) : (
          <div className="list-panel">
            <div className="list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="list-panel-collapse-btn" title="Collapse list"><ChevronLeft size={16} /></button>
              <div style={{ flex: 1, position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: '8px', top: '10px', color: 'var(--color-gray-400)' }} />
                <input className="form-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search users..." style={{ paddingLeft: '28px', width: '100%' }} />
              </div>
            </div>
            <div className="list-scroll">
              {filteredUsers.length === 0 ? (
                <div className="task-list-empty">No users found</div>
              ) : filteredUsers.map(u => (
                <button key={u.id} onClick={() => handleSelectUser(u)} className={`list-item-btn ${selected?.id === u.id ? 'selected' : ''}`}>
                  <span className="list-item-primary">{u.displayName}</span>
                  <span className="list-item-secondary">{u.email || 'No email'}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* DETAIL PANEL */}
        <div className="detail-panel">
          {selected && userDetail ? (
            <div className="detail-content" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

              {/* USER INFORMATION */}
              <div className="detail-grid-24" style={{ flexShrink: 0 }}>
                <div className="col-span-24"><div className="detail-section-header">User Information</div></div>
                <div className="form-group col-span-8">
                  <label>Display Name</label>
                  <input className={`form-input${isEditing && dirtyFields.has('displayName') ? ' dirty' : ''}`} value={isEditing ? editing.displayName : userDetail.displayName} onChange={isEditing ? e => handleFieldChange('displayName', e.target.value) : undefined} readOnly={!isEditing} />
                </div>
                <div className="form-group col-span-8">
                  <label>Email</label>
                  <input className={`form-input${isEditing && dirtyFields.has('email') ? ' dirty' : ''}`} value={isEditing ? editing.email : (userDetail.email || '')} onChange={isEditing ? e => handleFieldChange('email', e.target.value) : undefined} readOnly={!isEditing} />
                </div>
                <div className="form-group col-span-4">
                  <label>SysAdmin</label>
                  <div className="checkbox-field" style={{ height: '36px', display: 'flex', alignItems: 'center' }}>
                    <input type="checkbox" checked={isEditing ? !!editing.isSysAdmin : !!userDetail.isSysAdmin} onChange={isEditing ? e => handleFieldChange('isSysAdmin', e.target.checked) : undefined} disabled={!isEditing} style={{ width: '1rem', height: '1rem' }} />
                    {isEditing && dirtyFields.has('role') && <span style={{ color: '#f9a825', fontWeight: 600, fontSize: 'var(--text-xs)', marginLeft: '4px' }}>changed</span>}
                  </div>
                </div>
                <div className="form-group col-span-4">
                  <label>Active</label>
                  <div className="checkbox-field" style={{ height: '36px', display: 'flex', alignItems: 'center' }}>
                    <input type="checkbox" checked={isEditing ? editing.isActive : userDetail.isActive} onChange={isEditing ? e => handleFieldChange('isActive', e.target.checked) : undefined} disabled={!isEditing} style={{ width: '1rem', height: '1rem' }} />
                    {isEditing && dirtyFields.has('isActive') && <span style={{ color: '#f9a825', fontWeight: 600, fontSize: 'var(--text-xs)', marginLeft: '4px' }}>changed</span>}
                  </div>
                </div>
                <div className="form-group col-span-8">
                  <label>Organization</label>
                  {isEditing ? (
                    <select className={`form-input${dirtyFields.has('organizationId') ? ' dirty' : ''}`} value={editing.organizationId || ''} onChange={e => handleFieldChange('organizationId', e.target.value ? parseInt(e.target.value) : null)}>
                      <option value="">-- None --</option>
                      {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  ) : (
                    <input className="form-input" value={userDetail.organizationName || ''} readOnly />
                  )}
                </div>
                <div className="form-group col-span-8">
                  <label>Last Login</label>
                  <input className="form-input" value={userDetail.lastLogin ? new Date(userDetail.lastLogin).toLocaleString() : 'Never'} readOnly />
                </div>
                <div className="form-group col-span-8">
                  <label>Created</label>
                  <input className="form-input" value={userDetail.createdAt ? new Date(userDetail.createdAt).toLocaleDateString() : ''} readOnly />
                </div>
              </div>

              {/* INSTANCE ROLES */}
              <div className="related-tables-section" style={{ height: '200px', flexShrink: 0 }}>
                <div className="detail-section-header related-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Instance Roles ({userDetail.databaseRoles?.length || 0})</span>
                  {isEditing && (
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      {selectedDbRole && (
                        <button onClick={() => handleDeleteUserRole(selectedDbRole.id)} title="Remove selected role" style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Trash2 size={14} /></button>
                      )}
                      <button onClick={() => setShowCopyModal(true)} title="Copy from another user" style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Copy size={14} /></button>
                      <button onClick={() => setAddingRole({ UserId: selected.id, DatabaseId: 0, Role: 'User' })} title="Add role" style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Plus size={16} /></button>
                    </div>
                  )}
                </div>
                <div className="related-tables-content" style={{ flex: 1, overflow: 'hidden' }}>
                  {addingRole && (
                    <div style={{ display: 'flex', gap: '8px', padding: 'var(--spacing-sm) var(--spacing-md)', alignItems: 'center', borderBottom: '1px solid var(--color-gray-200)', flexShrink: 0 }}>
                      <select className="form-input" value={addingRole.DatabaseId} onChange={e => setAddingRole({ ...addingRole, DatabaseId: parseInt(e.target.value) })} style={{ width: '160px' }}>
                        <option value={0}>Select instance...</option>
                        {databases.filter(d => !userDetail.databaseRoles?.some(dr => dr.databaseId === d.id)).map(d => <option key={d.id} value={d.id}>{d.displayName}</option>)}
                      </select>
                      <select className="form-input" value={addingRole.Role} onChange={e => setAddingRole({ ...addingRole, Role: e.target.value })} style={{ width: '130px' }}>
                        <option value="Guest">Guest</option><option value="User">User</option><option value="PowerUser">PowerUser</option><option value="AppAdmin">AppAdmin</option>
                      </select>
                      <button className="btn btn-primary btn-sm" onClick={handleAddUserRole} disabled={!addingRole.DatabaseId}><Save size={14} /></button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setAddingRole(null)}><X size={14} /></button>
                    </div>
                  )}
                  <div className="related-table-grid-container" style={{ flex: 1 }}>
                    <div className="data-grid-container" style={{ border: 'none' }}>
                      <div className="data-grid-scroll-area">
                        <table className="data-grid" style={{ tableLayout: 'fixed' }}>
                          <thead><tr><th style={{ width: '120px' }}>Instance</th><th>Description</th><th style={{ width: '120px' }}>Role</th><th style={{ width: '100px' }}>Permissions</th></tr></thead>
                          <tbody>
                            {userDetail.databaseRoles?.map(dr => {
                              const displayRole = dirtyRoles[dr.id] || dr.role;
                              const isRoleDirty = !!dirtyRoles[dr.id];
                              return (
                                <tr key={dr.id} className="clickable" onClick={() => handleSelectDbRole(dr)} onDoubleClick={() => { if (isEditing) setEditingRoleId(dr.id); }} style={selectedDbRole?.id === dr.id ? { backgroundColor: 'var(--color-primary-light)' } : undefined}>
                                  <td>{dr.databaseName}</td>
                                  <td>{databases.find(d => d.id === dr.databaseId)?.description || ''}</td>
                                  <td><span style={{ fontWeight: 600, backgroundColor: isRoleDirty ? '#fffde7' : undefined, borderBottom: isRoleDirty ? '2px solid #f9a825' : undefined, padding: isRoleDirty ? '1px 4px' : undefined }}>{displayRole}</span></td>
                                  <td>{dr.overrides?.length || 0}</td>
                                </tr>
                              );
                            })}
                            {(!userDetail.databaseRoles || userDetail.databaseRoles.length === 0) && (
                              <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--color-gray-400)', padding: 'var(--spacing-lg)' }}>No roles assigned</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* INSTANCE PERMISSIONS */}
              <div className="related-tables-section" style={{ flex: 1, minHeight: '150px', marginTop: 'var(--spacing-md)', display: 'flex', flexDirection: 'column' }}>
                <div className="detail-section-header related-section-header" style={{ opacity: selectedDbRole ? 1 : 0.4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Instance Permissions{selectedDbRole ? ` — ${selectedDbRole.databaseName} (${selectedDbRole.role})` : ' (select instance role)'}</span>
                  {selectedDbRole && (
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <button onClick={() => setShowEffective(true)} title="View effective permissions" style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><ScanEye size={16} /></button>
                      {isEditing && (
                        <>
                          {selectedOverride && (
                            <button title="Remove selected permission" style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Trash2 size={14} /></button>
                          )}
                          <button title="Add permission" style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Plus size={16} /></button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="related-tables-content" style={{ flex: 1, overflow: 'hidden' }}>
                  {selectedDbRole ? (
                    <div className="related-table-grid-container" style={{ flex: 1 }}>
                      <div className="data-grid-container" style={{ border: 'none' }}>
                        <div className="data-grid-scroll-area">
                          <table className="data-grid">
                            <thead><tr><th>Permission</th><th>Category</th><th>Access</th></tr></thead>
                            <tbody>
                              {resolvedPerms ? (() => {
                                const overrides = resolvedPerms.permissions?.filter(p => p.source === 'UserOverride') || [];
                                if (overrides.length === 0) return <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--color-gray-400)', padding: 'var(--spacing-lg)' }}>No overrides — using {selectedDbRole.role} role defaults</td></tr>;
                                return overrides.map((p, idx) => (
                                  <tr key={idx} className="clickable" onClick={() => setSelectedOverride(selectedOverride?.permissionKey === p.permissionKey ? null : p)} style={selectedOverride?.permissionKey === p.permissionKey ? { backgroundColor: 'var(--color-primary-light)' } : undefined}>
                                    <td style={{ fontWeight: 500 }}>{allPermissions.find(ap => ap.permissionKey === p.permissionKey)?.displayName || p.permissionKey}</td>
                                    <td>{allPermissions.find(ap => ap.permissionKey === p.permissionKey)?.category || ''}</td>
                                    <td><span style={{ fontWeight: 600, color: p.access === 'Grant' ? 'var(--color-success)' : 'var(--color-error)' }}>{p.access}</span></td>
                                  </tr>
                                ));
                              })() : <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--color-gray-400)', padding: 'var(--spacing-lg)' }}>Loading...</td></tr>}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--color-gray-300)', fontSize: 'var(--text-sm)' }} />
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="dev-detail-empty">
              <Eye size={48} />
              <p>Select a user to view details</p>
              <p className="hint">Manage roles, permissions, and organization assignment</p>
            </div>
          )}
        </div>
      </div>

      {/* COPY FROM USER MODAL */}
      {showCopyModal && (
        <div className="sprint-add-task-overlay">
          <div className="sprint-add-task-modal" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Copy Roles & Permissions</h3>
              <button className="modal-close" onClick={() => { setShowCopyModal(false); setCopySource(null); }}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-lg)' }}>Copy roles and permission overrides from another user to <strong>{userDetail?.displayName}</strong>.</p>
              <div className="form-row" style={{ marginBottom: 'var(--spacing-lg)' }}>
                <label>Copy from</label>
                <select className="form-input" value={copySource?.id || ''} onChange={e => { const u = users.find(u => u.id === parseInt(e.target.value)); setCopySource(u || null); }}>
                  <option value="">Select a user...</option>
                  {users.filter(u => u.id !== selected?.id).map(u => <option key={u.id} value={u.id}>{u.displayName}{u.email ? ` (${u.email})` : ''}</option>)}
                </select>
              </div>
              <div className="form-row" style={{ marginBottom: 'var(--spacing-lg)' }}>
                <label>Scope</label>
                <select className="form-input" value={copyDbScope} onChange={e => setCopyDbScope(e.target.value)}>
                  <option value="all">All instances (full mirror)</option>
                  {databases.map(d => <option key={d.id} value={d.id}>{d.displayName} only</option>)}
                </select>
              </div>
              {copySource && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', padding: 'var(--spacing-sm)', backgroundColor: 'var(--color-gray-50)', borderRadius: 'var(--radius-sm)' }}>
                  {copyDbScope === 'all'
                    ? <span>This will replace ALL roles and permission overrides on <strong>{userDetail?.displayName}</strong> with those from <strong>{copySource.displayName}</strong>.</span>
                    : <span>This will copy the role and permissions for <strong>{databases.find(d => d.id === parseInt(copyDbScope))?.displayName}</strong> from <strong>{copySource.displayName}</strong>.</span>
                  }
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowCopyModal(false); setCopySource(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCopyFrom} disabled={!copySource || copySaving}>{copySaving ? 'Copying...' : 'Apply'}</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT INSTANCE ROLE MODAL */}
      {editingRoleId && (() => {
        const dr = userDetail?.databaseRoles?.find(r => r.id === editingRoleId);
        if (!dr) return null;
        const currentValue = dirtyRoles[dr.id] || dr.role;
        return (
          <div className="sprint-add-task-overlay">
            <div className="sprint-add-task-modal" style={{ maxWidth: '340px' }}>
              <div className="modal-header">
                <h3>Edit Role — {dr.databaseName}</h3>
                <button className="modal-close" onClick={() => setEditingRoleId(null)}><X size={20} /></button>
              </div>
              <div className="modal-body">
                <div className="form-row">
                  <label>Role</label>
                  <select className="form-input" value={currentValue} onChange={e => { const newRole = e.target.value; if (newRole === dr.role) { const next = { ...dirtyRoles }; delete next[dr.id]; setDirtyRoles(next); } else { setDirtyRoles({ ...dirtyRoles, [dr.id]: newRole }); } setEditingRoleId(null); }} autoFocus>
                    <option value="Guest">Guest</option><option value="User">User</option><option value="PowerUser">PowerUser</option><option value="AppAdmin">AppAdmin</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* EFFECTIVE PERMISSIONS MODAL */}
      {showEffective && selectedDbRole && resolvedPerms && (
        <div className="sprint-add-task-overlay">
          <div className="sprint-add-task-modal" style={{ maxWidth: '650px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h3>Effective Permissions — {userDetail?.displayName} / {selectedDbRole.databaseName}</h3>
              <button className="modal-close" onClick={() => setShowEffective(false)}><X size={20} /></button>
            </div>
            <div className="modal-body" style={{ flex: 1, overflow: 'auto', padding: 0 }}>
              <div style={{ padding: 'var(--spacing-sm) var(--spacing-lg)', fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', borderBottom: '1px solid var(--color-gray-200)' }}>
                Role: <strong>{resolvedPerms.resolvedRole}</strong> — showing all {resolvedPerms.permissions?.length || 0} permissions with final resolved access
              </div>
              <table className="data-grid" style={{ fontSize: 'var(--text-sm)' }}>
                <thead><tr><th>Permission</th><th>Category</th><th>Access</th><th>Source</th></tr></thead>
                <tbody>
                  {resolvedPerms.permissions?.map((p, idx) => (
                    <tr key={idx}>
                      <td style={{ fontWeight: 500 }}>{allPermissions.find(ap => ap.permissionKey === p.permissionKey)?.displayName || p.permissionKey}</td>
                      <td>{allPermissions.find(ap => ap.permissionKey === p.permissionKey)?.category || ''}</td>
                      <td><span style={{ fontWeight: 600, color: p.access === 'Grant' ? 'var(--color-success)' : 'var(--color-error)' }}>{p.access}</span></td>
                      <td style={{ color: p.source === 'UserOverride' ? 'var(--color-warning)' : 'var(--color-gray-400)' }}>{p.source === 'UserOverride' ? 'Override' : p.source === 'RoleDefault' ? 'Role Default' : 'No Entry'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowEffective(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
